import { ContentSwitch } from 'Components';
import search from 'constants/search';
import { copy } from 'helpers/deep';
import { getMainRoute } from 'helpers/title';
import { SUBMISSION_TYPES } from 'constants/submissionTypes';
import { cleanSearchParams, urlSearchToObject } from 'helpers/urlSearchParams';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import history from 'src/history';
import GlobalSearchDesktop from './GlobalSearchDesktop/GlobalSearchDesktop';
import GlobalSearchMobile from './GlobalSearchMobile/GlobalSearchMobile';

const searchRoute = ['jobs', 'templates', 'invoices', 'supplies'];
const normalizeFilterFieldValue = (filter) => {
  if (filter === 'SiteNumber') return 'SiteLocation';
  return filter;
};

class GlobalSearch extends Component {
  constructor(props) {
    super(props);

    const stateFromSearch = urlSearchToObject(history.location.search);

    const formData = Object.assign(
      {},
      search.initialFormData,
      {
        [normalizeFilterFieldValue(
          props.filterFieldValue,
        )]: props.filterItemValue,
      },
      stateFromSearch,
    );

    if (props.isInternalUser) {
      formData.ClientNumber = props.clientNumber;
    }

    const currentRoute = getMainRoute();

    // Internal users may not reselect ClientNumber in global search
    const clientOptions = this.props.isInternalUser
      ? this.props.clients.filter(
          (item) => +item.value === +this.props.clientNumber,
        )
      : [
          {
            value: '',
            label: i18n.t('globalSearch:Select All'),
          },
          ...this.props.clients,
        ];

    const searchTypes = search.searchType.map((x) => ({
      ...x,
      label: i18n.t(`globalSearch:${x.label}`),
    }));

    this.requiredEnvBulk = ['supplies', 'invoices'];

    this.state = {
      searchType: searchRoute.includes(currentRoute) ? currentRoute : 'jobs',
      exactMatch: stateFromSearch.exactMatch === 'true',
      formData,
      submissionTypeOptions: [
        {
          value: '',
          label: i18n.t('globalSearch:Select All'),
        },
        ...this.props.submissionTypeOptions,
      ],
      clientOptions,
      dateTypeOptions: [
        {
          label: i18n.t('globalSearch:Created Date'),
          value: 'createdDate',
        },
        {
          label: i18n.t('globalSearch:Modified Date'),
          value: 'modified Date',
        },
      ],
      dateTypeJobsOptions: [
        {
          label: i18n.t('globalSearch:Received Date'),
          value: 'receivedDate',
        },
        {
          label: i18n.t('globalSearch:Sampling Date'),
          value: 'sampleDate',
        },
        {
          label: i18n.t('globalSearch:Job Due Date'),
          value: 'jobDue',
        },
        {
          label: i18n.t('globalSearch:Submitted Date'),
          value: 'submitted',
        },
        {
          label: i18n.t('additionalAnalysis:DisposalDate'),
          value: 'disposalDate',
        },
      ].filter((item) => {
        if (!this.props.hasAccessToAA && item.value === 'disposalDate') {
          return false;
        }

        return true;
      }),
      dateTypeOrderOptions: [
        {
          label: i18n.t('globalSearch:Date Ordered'),
          value: 'Date Ordered',
        },
        {
          label: i18n.t('globalSearch:Date Shipped'),
          value: 'Date Shipped',
        },
        {
          label: i18n.t('globalSearch:Date Required'),
          value: 'Date Required',
        },
      ],
      invoiceStatusOptions: [
        {
          value: '',
          label: i18n.t('globalSearch:Select All'),
        },
        {
          value: 'No',
          label: i18n.t('globalSearch:Balance Due'),
        },
        {
          value: 'Yes',
          label: i18n.t('globalSearch:Paid'),
        },
      ],
      jobStatusOptions: [
        {
          value: '',
          label: i18n.t('globalSearch:Select All'),
        },
        {
          value: '0',
          label: i18n.t('globalSearch:Entered'),
        },
        {
          value: '1',
          label: i18n.t('globalSearch:In Progress'),
        },
        {
          value: '5',
          label: i18n.t('globalSearch:Completed'),
        },
        {
          value: 'C',
          label: i18n.t('globalSearch:Cancelled'),
        },
        {
          value: 'D',
          label: i18n.t('globalSearch:Draft'),
        },
        {
          value: 'E',
          label: i18n.t('globalSearch:Editing'),
        },
        {
          value: 'I',
          label: i18n.t('globalSearch:Importing'),
        },
        {
          value: 'S',
          label: i18n.t('globalSearch:Submitted'),
        },
      ],
      searchOptions: this.props.hasAccessToInvoices
        ? searchTypes
        : searchTypes.filter((x) => x.value !== 'invoices'),
    };

    this.handleSearchTypeChange = this.handleSearchTypeChange.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleToggleExact = this.handleToggleExact.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleToggleExact() {
    this.setState((prevState) => ({ exactMatch: !prevState.exactMatch }));
  }

  handleClear() {
    this.setState({ formData: search.initialFormData });
  }

  handleFormChange(e) {
    const { name, value } = e.target;

    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleSearchTypeChange(e) {
    this.setState({ searchType: e.target.value });
  }

  handleSubmit(e) {
    e.preventDefault();
    const validSearchTypes = ['jobs', 'templates', 'supplies', 'invoices'];
    const { formData, searchType, exactMatch } = this.state;

    this.props.handleClose();

    if (validSearchTypes.includes(searchType)) {
      const formConfig = copy(search.formConfig[searchType]);
      const relevantSearchParams = formConfig.reduce((acc, item) => {
        if (item.name !== undefined) {
          return {
            ...acc,
            [item.name]:
              formData[item.name] === '' ? undefined : formData[item.name],
          };
        }
        return acc;
      }, {});

      const searchParams = new URLSearchParams(
        cleanSearchParams(relevantSearchParams, { searchType, exactMatch }),
      );
      history.push(`/${searchType}?${searchParams.toString()}`);
    }
  }

  render() {
    const { state, props } = this;

    const sharedProps = {
      domRef: this.props.domRef,
      onFormChange: this.handleFormChange,
      onSubmit: this.handleSubmit,
      onToggleExact: this.handleToggleExact,
      onClear: this.handleClear,
      onSearchTypeChange: this.handleSearchTypeChange,
      submissionTypeOptions: !this.requiredEnvBulk.includes(state.searchType)
        ? state.submissionTypeOptions.filter(
            (item) => item.value !== SUBMISSION_TYPES.ENV_BULK,
          )
        : state.submissionTypeOptions,
    };

    return (
      <ContentSwitch
        index={props.deviceIndex}
        {...props}
        {...state}
        {...sharedProps}
      >
        <GlobalSearchMobile />
        <GlobalSearchDesktop />
      </ContentSwitch>
    );
  }
}

GlobalSearch.propTypes = {
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  submissionTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      options: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  hasAccessToInvoices: PropTypes.bool,
  domRef: PropTypes.shape({}),
  handleClose: PropTypes.func,
  filterFieldValue: PropTypes.string,
  filterItemValue: PropTypes.string,
  isInternalUser: PropTypes.bool,
  clientNumber: PropTypes.string,
  hasAccessToAA: PropTypes.bool,
};

export default GlobalSearch;



// WEBPACK FOOTER //
// ./src/components/GlobalSearch/GlobalSearch.jsx